<script>
import { mapGetters } from 'vuex';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import MessageApi from 'dashboard/api/inbox/message';
import Spinner from 'shared/components/Spinner.vue';
import Thumbnail from '../Thumbnail.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';

export default {
  components: {
    Spinner,
    Thumbnail,
    TimeAgo,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    messageData: {
      type: Object,
      required: true,
    },
  },
  emits: ['close', 'forward'],
  data() {
    return {
      selectedConversationId: null,
      forwardInSameConversation: false,
      availableConversations: [],
      isLoadingConversations: false,
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      conversations: 'conversations/getAllConversations',
      inboxes: 'inboxes/getInboxes',
    }),
    currentConversationId() {
      // Safe guard: ensure messageData exists
      if (!this.messageData) {
        return null;
      }

      // Handle different data structures:
      // 1. Direct properties on messageData (conversationId, conversation_id)
      // 2. Properties from event data (conversationId from emit)
      // 3. Properties from message object nested inside messageData
      return (
        this.messageData.conversationId ||
        this.messageData.conversation_id ||
        this.messageData.message?.conversation_id ||
        this.messageData.message?.conversationId
      );
    },
    currentChannelType() {
      // Lấy trực tiếp từ messageData thay vì phụ thuộc vào store
      if (this.messageData?.message?.inbox_id) {
        const targetInbox = this.inboxes.find(
          inbox => inbox.id === this.messageData.message.inbox_id
        );
        return targetInbox?.channel_type;
      }

      // Fallback: tìm từ conversations store
      if (!Array.isArray(this.conversations) || !Array.isArray(this.inboxes)) {
        return null;
      }

      const currentConversation = this.conversations.find(
        conv => conv.id === parseInt(this.currentConversationId, 10)
      );

      if (currentConversation) {
        const inboxForConversation = this.inboxes.find(
          inbox => inbox.id === currentConversation.inbox_id
        );
        return inboxForConversation?.channel_type;
      }

      return null;
    },
    filteredConversations() {
      if (!this.searchQuery.trim()) {
        return this.availableConversations;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.availableConversations.filter(conversation => {
        const contactName = this.getContactName(conversation).toLowerCase();
        const lastMessage = this.getLastMessage(conversation).toLowerCase();
        return (
          contactName.includes(query) ||
          lastMessage.includes(query) ||
          conversation.id.toString().includes(query)
        );
      });
    },
    modalTitle() {
      return this.$t('CONVERSATION.FORWARD_MESSAGE.TITLE');
    },
    modalContent() {
      return this.$t('CONVERSATION.FORWARD_MESSAGE.DESCRIPTION');
    },
    channelTypeName() {

      switch (this.currentChannelType) {
        case INBOX_TYPES.ZALO_PERSONAL:
          return 'Zalo Personal';
        case INBOX_TYPES.ZALOA:
          return 'Zalo OA';
        case INBOX_TYPES.WHATSAPP:
          return 'WhatsApp';
        case INBOX_TYPES.EMAIL:
          return 'Email';
        case INBOX_TYPES.FB:
          return 'Facebook';
        case INBOX_TYPES.TELEGRAM:
          return 'Telegram';
        default:
          return this.currentChannelType || 'Unknown';
      }
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadConversationsByChannelType();
      } else {
        // Reset search when modal closes
        this.searchQuery = '';
      }
    },
    currentChannelType(newVal) {
      if (newVal && this.show) {
        this.loadConversationsByChannelType();
      }
    },
  },
  mounted() {
    // Load inboxes trước
    this.$store.dispatch('inboxes/get').then(() => {
      if (this.show) {
        this.loadConversationsByChannelType();
      }
    });
  },
  methods: {
    onClose() {
      this.resetForm();
      this.$emit('close');
    },
    resetForm() {
      this.selectedConversationId = null;
      this.forwardInSameConversation = false;
      this.searchQuery = '';
    },
    onForward() {
      const targetConversationId = this.forwardInSameConversation
        ? this.currentConversationId
        : this.selectedConversationId;

      // Handle different data structures for messageId
      const messageId =
        this.messageData.id ||
        this.messageData.messageId ||
        this.messageData.message?.id;

      this.$emit('forward', {
        messageId,
        conversationId: this.currentConversationId,
        targetConversationId,
      });
      this.onClose();
    },
    isFormValid() {
      return this.forwardInSameConversation || this.selectedConversationId;
    },
    async loadConversationsByChannelType() {
      const inboxId = this.messageData?.message?.inbox_id;

      if (!inboxId) {
        this.availableConversations = [];
        return;
      }

      this.isLoadingConversations = true;
      try {
        const response = await MessageApi.getConversationsByInbox(inboxId);

        // Filter ra conversation hiện tại
        this.availableConversations = response.data.filter(
          conv => conv.id !== parseInt(this.currentConversationId, 10)
        );
      } catch (error) {
        this.availableConversations = [];
      } finally {
        this.isLoadingConversations = false;
      }
    },
    getContactName(conversation) {
      return conversation.meta?.sender?.name || 'Unknown Contact';
    },
    getContactAvatar(conversation) {
      return conversation.meta?.sender?.thumbnail || '';
    },
    getContactStatus(conversation) {
      return conversation.meta?.sender?.availability_status || 'offline';
    },
    getLastMessage(conversation) {
      if (!conversation.messages || !conversation.messages.length) {
        return 'No messages';
      }
      const lastMessage =
        conversation.messages[conversation.messages.length - 1];
      return lastMessage.content || 'Attachment';
    },
    getUnreadCount(conversation) {
      return conversation.unread_count || 0;
    },
    getConversationTime(conversation) {
      return conversation.timestamp || conversation.created_at;
    },
    getInboxBadge(conversation) {
      const targetInbox = this.inboxes.find(
        inbox => inbox.id === conversation.inbox_id
      );
      return targetInbox?.channel_type || 'api';
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose" size="medium">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalContent"
    />
    <form
      class="flex flex-col w-full modal-content"
      @submit.prevent="onForward"
    >
      <!-- Channel info banner -->
      <div
        v-if="currentChannelType"
        class="w-full mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
      >
        <div class="flex items-start gap-3">
          <fluent-icon
            icon="info"
            size="16"
            class="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
          />
          <div>
            <p
              class="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1"
            >
              {{ $t('CONVERSATION.FORWARD_MESSAGE.CHANNEL_INFO') }}
            </p>
            <p class="text-xs text-blue-700 dark:text-blue-400">
              {{
                $t('CONVERSATION.FORWARD_MESSAGE.CHANNEL_RESTRICTION', {
                  channelType: channelTypeName,
                })
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Forward options -->
      <div class="w-full mb-6">
        <h4
          class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4"
        >
          {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECT_DESTINATION') }}
        </h4>

        <div class="space-y-3">
          <!-- Forward to same conversation -->
          <label
            class="flex items-start gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
          >
            <input
              v-model="forwardInSameConversation"
              type="radio"
              :value="true"
              class="mt-1 w-4 h-4 text-woot-600 border-slate-300 dark:border-slate-600 focus:ring-woot-500 focus:ring-2"
              name="forwardDestination"
            />
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <fluent-icon
                  icon="arrow-reply"
                  size="16"
                  class="text-slate-600 dark:text-slate-400"
                />
                <span
                  class="text-sm font-medium text-slate-900 dark:text-slate-100"
                >
                  {{ $t('CONVERSATION.FORWARD_MESSAGE.FORWARD_TO_SAME') }}
                </span>
              </div>
              <p class="text-xs text-slate-600 dark:text-slate-400 ml-6">
                {{
                  $t('CONVERSATION.FORWARD_MESSAGE.FORWARD_TO_SAME_DESCRIPTION')
                }}
              </p>
            </div>
          </label>

          <!-- Forward to different conversation -->
          <label
            class="flex items-start gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
          >
            <input
              v-model="forwardInSameConversation"
              type="radio"
              :value="false"
              class="mt-1 w-4 h-4 text-woot-600 border-slate-300 dark:border-slate-600 focus:ring-woot-500 focus:ring-2"
              name="forwardDestination"
            />
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <fluent-icon
                  icon="chat-multiple"
                  size="16"
                  class="text-slate-600 dark:text-slate-400"
                />
                <span
                  class="text-sm font-medium text-slate-900 dark:text-slate-100"
                >
                  {{ $t('CONVERSATION.FORWARD_MESSAGE.FORWARD_TO_OTHER') }}
                </span>
              </div>
              <p class="text-xs text-slate-600 dark:text-slate-400 ml-6">
                {{
                  $t(
                    'CONVERSATION.FORWARD_MESSAGE.FORWARD_TO_OTHER_DESCRIPTION'
                  )
                }}
              </p>
            </div>
          </label>
        </div>
      </div>

      <!-- Conversation selection -->
      <div v-if="!forwardInSameConversation" class="w-full">
        <div class="flex items-center justify-between mb-4">
          <h4 class="text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECT_CONVERSATION') }}
          </h4>
          <span
            v-if="!isLoadingConversations"
            class="text-xs text-slate-500 dark:text-slate-400"
          >
            {{
              $t('CONVERSATION.FORWARD_MESSAGE.TOTAL_CONVERSATIONS', {
                count: availableConversations.length,
              })
            }}
          </span>
        </div>

        <!-- Search box -->
        <div v-if="availableConversations.length > 0" class="relative mb-4">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('CONVERSATION.FORWARD_MESSAGE.SEARCH_PLACEHOLDER')"
            class="block w-full pl-10 pr-10 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 text-sm"
          />
          <div
            v-if="searchQuery"
            class="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <button
              type="button"
              class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              @click="searchQuery = ''"
            >
              <fluent-icon icon="dismiss" size="16" />
            </button>
          </div>
        </div>

        <!-- Loading state -->
        <div
          v-if="isLoadingConversations"
          class="flex flex-col items-center justify-center py-12 rounded-lg bg-slate-50 dark:bg-slate-800"
        >
          <Spinner size="small" />
          <span class="mt-3 text-sm text-slate-600 dark:text-slate-400">
            {{ $t('CONVERSATION.FORWARD_MESSAGE.LOADING_CONVERSATIONS') }}
          </span>
        </div>

        <!-- Conversations list -->
        <div
          v-else-if="filteredConversations.length > 0"
          class="max-h-96 overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
        >
          <div
            class="sticky top-0 p-3 border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 z-10"
          >
            <p
              class="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide"
            >
              {{ $t('CONVERSATION.FORWARD_MESSAGE.AVAILABLE_CONVERSATIONS') }}
              ({{ filteredConversations.length }})
            </p>
          </div>
          <div class="divide-y divide-slate-100 dark:divide-slate-700">
            <label
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              class="relative flex items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group"
              :class="{
                'bg-woot-50 dark:bg-woot-800/30 ring-2 ring-woot-500/20':
                  selectedConversationId === conversation.id,
              }"
            >
              <input
                v-model="selectedConversationId"
                type="radio"
                :value="conversation.id"
                class="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-woot-600 border-slate-300 dark:border-slate-600 focus:ring-woot-500 focus:ring-2"
                name="selectedConversation"
              />

              <!-- Custom conversation item -->
              <div class="flex items-center w-full pl-12 pr-4 py-4">
                <!-- Contact Avatar -->
                <div class="flex-shrink-0 mr-4">
                  <Thumbnail
                    :src="getContactAvatar(conversation)"
                    :badge="getInboxBadge(conversation)"
                    :username="getContactName(conversation)"
                    :status="getContactStatus(conversation)"
                    size="44px"
                  />
                </div>

                <!-- Conversation Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between mb-1.5">
                    <h4
                      class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate"
                    >
                      {{ getContactName(conversation) }}
                    </h4>
                    <div class="flex items-center gap-2 ml-3">
                      <!-- Unread count -->
                      <span
                        v-if="getUnreadCount(conversation) > 0"
                        class="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-green-500 rounded-full"
                      >
                        {{
                          getUnreadCount(conversation) > 9
                            ? '9+'
                            : getUnreadCount(conversation)
                        }}
                      </span>
                      <!-- Time -->
                      <span class="text-xs text-slate-500 dark:text-slate-400">
                        <TimeAgo
                          :last-activity-timestamp="
                            getConversationTime(conversation)
                          "
                          :created-at-timestamp="conversation.created_at"
                        />
                      </span>
                    </div>
                  </div>

                  <!-- Last message -->
                  <p
                    class="text-xs text-slate-600 dark:text-slate-400 truncate mb-2"
                  >
                    {{ getLastMessage(conversation) }}
                  </p>

                  <!-- Conversation status and assignee -->
                  <div class="flex items-center gap-2">
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                      :class="{
                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300':
                          conversation.status === 'open',
                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300':
                          conversation.status === 'resolved',
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300':
                          conversation.status === 'pending',
                        'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300':
                          conversation.status === 'snoozed',
                      }"
                    >
                      {{ conversation.status || 'open' }}
                    </span>

                    <!-- Assignee -->
                    <span
                      v-if="conversation.meta?.assignee?.name"
                      class="text-xs text-slate-500 dark:text-slate-400 flex items-center"
                    >
                      <fluent-icon
                        icon="person"
                        size="12"
                        class="inline mr-1"
                      />
                      {{ conversation.meta.assignee.name }}
                    </span>
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>

        <!-- Empty search results -->
        <div
          v-else-if="searchQuery && !isLoadingConversations"
          class="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800"
        >
          <div class="flex flex-col items-center">
            <fluent-icon
              icon="search"
              size="48"
              class="text-slate-300 dark:text-slate-600 mb-4"
            />
            <h5
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
            >
              {{ $t('CONVERSATION.FORWARD_MESSAGE.NO_SEARCH_RESULTS') }}
            </h5>
            <p class="text-xs text-slate-500 dark:text-slate-500 max-w-sm mb-3">
              {{
                $t('CONVERSATION.FORWARD_MESSAGE.NO_SEARCH_RESULTS_DESC', {
                  query: searchQuery,
                })
              }}
            </p>
            <woot-button variant="clear" size="small" @click="searchQuery = ''">
              {{ $t('CONVERSATION.FORWARD_MESSAGE.CLEAR_SEARCH') }}
            </woot-button>
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!isLoadingConversations"
          class="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800"
        >
          <div class="flex flex-col items-center">
            <fluent-icon
              icon="chat-multiple"
              size="48"
              class="text-slate-300 dark:text-slate-600 mb-4"
            />
            <h5
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
            >
              {{ $t('CONVERSATION.FORWARD_MESSAGE.NO_CONVERSATIONS_TITLE') }}
            </h5>
            <p class="text-xs text-slate-500 dark:text-slate-500 max-w-sm">
              {{
                $t('CONVERSATION.FORWARD_MESSAGE.NO_CONVERSATIONS_AVAILABLE')
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div
        class="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-slate-200 dark:border-slate-600"
      >
        <woot-button variant="clear" @click.prevent="onClose">
          {{ $t('CONVERSATION.FORWARD_MESSAGE.CANCEL') }}
        </woot-button>
        <woot-button
          :is-disabled="!isFormValid()"
          icon="arrow-forward"
          type="submit"
        >
          {{ $t('CONVERSATION.FORWARD_MESSAGE.FORWARD') }}
        </woot-button>
      </div>
    </form>
  </woot-modal>
</template>

<style lang="scss" scoped>
.modal-content {
  @apply pt-2 px-8 pb-8;
}

// Custom radio button styling to match Hichat design
input[type='radio'] {
  @apply appearance-none w-4 h-4 border-2 border-slate-300 dark:border-slate-600 rounded-full bg-white dark:bg-slate-800;
  @apply focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-800;
  @apply checked:bg-woot-500 checked:border-woot-500;
  @apply transition-all duration-200;

  &:checked {
    background-image: radial-gradient(circle, white 30%, transparent 30%);
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
  }

  &:hover:not(:disabled) {
    @apply border-woot-400;
  }
}

// Smooth transitions for all interactive elements
label {
  @apply transition-all duration-200 ease-in-out;
}

// Custom conversation item styling
.conversation-item {
  &:hover {
    @apply bg-slate-50 dark:bg-slate-700/30;
  }

  &.selected {
    @apply bg-woot-50 dark:bg-woot-800/20 ring-1 ring-woot-500/30;
  }
}
</style>
